<template>
	<!-- Every recorded visit of one short link (campaign recipient or test link), newest first. -->
	<div class="sv-timeline">
		<div v-if="loading" class="sv-empty"><i class="fas fa-spinner fa-spin me-1"></i> Loading visits…</div>
		<div v-else-if="error" class="sv-empty text-danger">{{ error }}</div>
		<div v-else-if="!visits.length" class="sv-empty">No visits yet.</div>

		<ol v-else class="sv-list">
			<li v-for="visit in visits" :key="visit.id" class="sv-item" :class="{ 'sv-item--repeat': visit.is_repeat }">
				<span class="sv-dot" :class="`sv-dot--${visit.channel}`" aria-hidden="true"></span>
				<div class="sv-main">
					<div class="sv-head">
						<strong>{{ formatDateTime(visit.visited_at) }}</strong>
						<span class="sv-pill" :class="`sv-pill--${visit.channel}`">{{ channelLabel(visit.channel) }}</span>
						<span
							v-if="visit.is_repeat"
							class="sv-pill sv-pill--repeat"
							:title="`Same visitor within ${repeatWindow} minutes of a counted visit: shown here, not counted, no Slack message`"
							>Repeat</span
						>
					</div>
					<div class="sv-meta">
						<span v-if="visit.device"><i class="fas fa-mobile-alt me-1"></i>{{ visit.device }}</span>
						<span v-if="visit.location" title="Approximate, from the IP address"><i class="fas fa-map-marker-alt me-1"></i>{{ visit.location }}</span>
						<span v-if="visit.visitor" title="Same code = same network/device (hashed IP)"><i class="fas fa-fingerprint me-1"></i>{{ visit.visitor }}</span>
					</div>
					<div v-if="visit.destination" class="sv-destination" :title="visit.destination">{{ visit.destination }}</div>
				</div>
			</li>
		</ol>

		<div v-if="!loading && visits.length" class="sv-footer">
			{{ countedVisits }} counted {{ countedVisits === 1 ? 'visit' : 'visits' }}<template v-if="countedVisits !== visits.length">
				+ {{ visits.length - countedVisits }} repeat (same visitor within {{ repeatWindow }} min)</template
			>
			· visits older than {{ retentionYears }} years are removed from this timeline
			(the first visit and the visit count stay on the recipient).
		</div>
	</div>
</template>

<script>
import restApi from '@/api/index'
import { formatUtc } from '@/utils/time'

export default {
	name: 'ShortlinkVisitTimeline',
	props: {
		// auto_item | delivery_object | test
		type: { type: String, required: true },
		id: { type: [Number, String], required: true },
	},
	data() {
		return { loading: false, error: null, visits: [], retentionYears: 2, repeatWindow: 30 }
	},
	computed: {
		countedVisits() {
			return this.visits.filter(visit => !visit.is_repeat).length
		},
	},
	watch: {
		id: 'load',
	},
	created() {
		this.load()
	},
	methods: {
		async load() {
			this.loading = true
			this.error = null
			try {
				const { data } = await restApi.get('/shortlink-visits', { params: { type: this.type, id: this.id } })
				this.visits = data.visits || []
				this.retentionYears = data.retention_years || 2
				this.repeatWindow = data.repeat_window_minutes || 30
			} catch (error) {
				this.error = (error.response && error.response.data && error.response.data.message) || 'Could not load visits.'
			} finally {
				this.loading = false
			}
		},
		channelLabel(channel) {
			if (channel === 'qr') return 'QR scan'
			if (channel === 'typed') return 'Typed link'
			return 'Unknown'
		},
		formatDateTime(value) {
			return formatUtc(value, 'MMM D, YYYY h:mm A')
		},
	},
}
</script>

<style scoped>
.sv-empty {
	padding: 12px 0;
	color: #8a909c;
	font-size: 13px;
}

.sv-list {
	position: relative;
	margin: 0;
	padding: 0 0 0 18px;
	list-style: none;
}

.sv-list::before {
	content: '';
	position: absolute;
	top: 6px;
	bottom: 6px;
	left: 4px;
	width: 2px;
	background: #e6e8ec;
}

.sv-item {
	position: relative;
	padding: 0 0 14px;
}

.sv-dot {
	position: absolute;
	top: 5px;
	left: -18px;
	width: 10px;
	height: 10px;
	border: 2px solid #fff;
	border-radius: 50%;
	background: #94a3b8;
	box-shadow: 0 0 0 1px #e6e8ec;
}

.sv-dot--qr {
	background: #1d4ed8;
}

.sv-dot--typed {
	background: #15803d;
}

.sv-head {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: #2b303b;
}

.sv-pill {
	padding: 2px 8px;
	border-radius: 999px;
	background: #f1f5f9;
	color: #64748b;
	font-size: 11px;
	font-weight: 700;
}

.sv-pill--qr {
	background: #dbeafe;
	color: #1d4ed8;
}

.sv-pill--repeat {
	background: #f1f5f9;
	color: #8a909c;
}

.sv-item--repeat .sv-head,
.sv-item--repeat .sv-meta {
	opacity: 0.6;
}

.sv-pill--typed {
	background: #dcfce7;
	color: #166534;
}

.sv-meta {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 14px;
	margin-top: 3px;
	color: #596170;
	font-size: 12px;
}

.sv-destination {
	margin-top: 2px;
	overflow: hidden;
	color: #8a909c;
	font-size: 11px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.sv-footer {
	padding-top: 6px;
	border-top: 1px solid #f1f3f5;
	color: #8a909c;
	font-size: 11px;
}
</style>
